import api from '../services/api';

// The 'achievement' level is shown to people as Completion.
export const LEVEL_NAMES = { achievement: 'Completion', attendance: 'Attendance' };

// A trainer's choice of certificates for one person.
export const AWARD_NAMES = { both: 'Completion + Attendance', attendance: 'Attendance only', none: 'None' };

export const THEME_NAMES = {
  modern: 'Modern', classic: 'Classic', banner: 'Banner', corner: 'Corner', minimal: 'Minimal', custom: 'Custom background'
};

// Starting points for a new template; everything stays editable.
export const PRESETS = {
  achievement: {
    name: 'Certificate of Completion', theme: 'modern', accent_color: '#d9661f',
    kicker: 'Certificate of Completion', heading: '{course}', lead_text: 'This is to certify that',
    statement: 'has successfully completed {course} and passed the assessment.', details: '{dates}',
    company_text: 'Conducted for {company}', number_prefix: 'LP',
    show_number: true, show_employee_id: false, show_score: false,
    signatories: [{ name: '', title: '' }, { name: '', title: '' }]
  },
  attendance: {
    name: 'Certificate of Attendance', theme: 'modern', accent_color: '#d9661f',
    kicker: 'Certificate of Attendance', heading: '{course}', lead_text: 'This certificate is awarded to',
    statement: 'for attendance at the instructor-led training {course}.', details: '{dates}',
    company_text: 'Conducted for {company}', number_prefix: 'LP-ATT',
    show_number: true, show_employee_id: false, show_score: false,
    signatories: [{ name: '', title: '' }, { name: '', title: '' }]
  }
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

// Error bodies come back as blobs too; read the message out of them.
const blobError = async (err, fallback) => {
  try {
    const text = await err.response.data.text();
    return JSON.parse(text).error || fallback;
  } catch {
    return fallback;
  }
};

export const download = async (path, filename, fallback = 'Download failed') => {
  try {
    const r = await api.get(path, { responseType: 'blob' });
    saveBlob(r.data, filename);
  } catch (err) {
    alert(await blobError(err, fallback));
  }
};

// Open a PDF in a new tab. The tab is opened first so pop-up blockers allow it.
export const openPdf = async (request, fallback = 'Could not open the PDF') => {
  const win = window.open('', '_blank');
  try {
    const r = await request({ responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    if (win) win.location.href = url; else saveBlob(r.data, 'certificate.pdf');
  } catch (err) {
    if (win) win.close();
    alert(await blobError(err, fallback));
  }
};

// "1 Certificate of Completion + 2 Certificates of Attendance"
export const certCounts = (ach, att) => [
  ach ? `${ach} Certificate${ach === 1 ? '' : 's'} of Completion` : '',
  att ? `${att} Certificate${att === 1 ? '' : 's'} of Attendance` : ''
].filter(Boolean).join(' + ') || 'no certificates';

export const confirmIssue = (people, ach, att) => window.confirm(
  `Issue certificates for ${people} ${people === 1 ? 'person' : 'people'}:\n\n${certCounts(ach, att)}\n\n` +
  'Nothing will be emailed. You can open and check them first, then email the ones you choose.'
);

export const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

// Email exactly the ticked certificates, after confirmation. Nothing is ever
// sent without this.
export const emailSelected = async (path, certs, done) => {
  if (!certs.length) return;
  // One email per person, with all of their selected certificates attached.
  const people = new Map();
  certs.forEach(c => {
    const key = `${(c.recipient_email || '').toLowerCase()}|${c.recipient_name}`;
    people.set(key, [...(people.get(key) || []), c]);
  });
  const lines = [...people.values()].map(g =>
    `• ${g[0].recipient_name} <${g[0].recipient_email || 'no email'}> — ${g.map(c => c.level === 'attendance' ? 'Attendance' : 'Completion').join(' + ')}`);
  const list = lines.slice(0, 8).join('\n') + (lines.length > 8 ? `\n…and ${lines.length - 8} more` : '');
  const again = certs.filter(c => c.emailed_at).length;
  if (!window.confirm(
    `Send ${people.size} email${people.size > 1 ? 's' : ''} (${certs.length} certificate${certs.length > 1 ? 's' : ''} as PDF attachments) to:\n\n${list}` +
    `${again ? `\n\n${again} of these certificates have been emailed before.` : ''}\n\nSend now?`
  )) return;
  try {
    const { data } = await api.post(path, { certificate_ids: certs.map(c => c.id) });
    const failed = data.failed || [];
    alert(`${data.emails ?? data.sent} email${(data.emails ?? data.sent) === 1 ? '' : 's'} sent (${data.sent} certificate${data.sent === 1 ? '' : 's'})` +
      `${failed.length ? `\n\n${failed.length} not sent:\n${failed.map(f => `${f.name}: ${f.error}`).join('\n')}` : ''}`);
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to send');
  }
  done();
};
